// Used in view-composite-assignment.component.html and view-assignment.component.html

// Angular
import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from "@angular/core";
import {
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";

// RXJS/Store
import * as fromCourseStore from "#core/teacher-classes/store";
import { select, Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

// Services/Types/Helpers
import { DateFormatterHelper } from "#core/helpers";
import { AssignmentHelper } from "#helpers/assignment.helper";
import { DurationFormat } from "#helpers/durationFormat.helper";
import { LanguageHelper } from "#helpers/language.helper";
import {
    AssetTypeHelper,
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
    PPUser as User,
} from "cli-types";

// Components
import { ActivityPreviewDialogComponent } from "#components/dialog-boxes/activity-preview-dialog/activity-preview-dialog.component";
import { ReadycheckReportDialogComponent } from "#components/dialog-boxes/readycheck-report-dialog/readycheck-report-dialog.component";
import { Asset, assetActivityTypeIds, assetTypeIds, Bundle, ExtLink } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-cure";
import { ASSETS_SERVICE } from "@carnegie-learning/data-access-angular-cure";

@Component({
    selector: "workspace-details",
    templateUrl: "./workspace-details.component.html",
    styleUrls: [
        "./workspace-details.component.scss",
        "../../../styles/shared-styles.scss",
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class WorkspaceDetailsComponent implements OnInit, OnDestroy, OnChanges {
    activityPreviewDialogRef?: MatDialogRef<ActivityPreviewDialogComponent> | null;
    readyCheckReportDialogRef?: MatDialogRef<ReadycheckReportDialogComponent> | null;
    workspaceStatus = "";
    workspaceActivities: Assignment[] | CompositeTaskAssignment[] = [];
    practiceAttempts = 0;

    readonly assetTypeIds = assetTypeIds;
    readonly assetActivityTypeIds = assetActivityTypeIds;

    @Input() compositeTaskAssignments?: CompositeTaskAssignment[];
    @Input() assignment?: Assignment | CompositeTaskAssignment;
    @Input() sectionID = "";
    @Input() course?: Course;
    @Input() userProfile?: User;
    @Input() bundleWithEbook = false;
    @Input() maxAttemptsUpdated = false;

    @Output() resetBundleActivityStatusFn = new EventEmitter<number>();

    private ngUnsubscribe = new Subject();

    constructor(
        private dateFormatterHelper: DateFormatterHelper,
        private assetTypeHelper: AssetTypeHelper,
        private languageHelper: LanguageHelper,
        private durationFormat: DurationFormat,
        public dialog: MatDialog,
        private courseStore: Store<fromCourseStore.CoursesState>,
        private assignmentHelper: AssignmentHelper,
        @Inject(ASSETS_SERVICE) private assetsService: AssetsService,
    ) {}

    ngOnInit() {
        if (this.assignment) {
            if (this.isCompositeActivity()) {
                this.workspaceActivities = [
                    <CompositeTaskAssignment>this.assignment,
                ];
            } else if (this.isComposite() && this.compositeTaskAssignments) {
                this.workspaceActivities =
                    this.assignmentHelper.sortCompositeTasksByAsset(
                        this.compositeTaskAssignments,
                        <Bundle>this.assignment.asset
                    );
            } else {
                this.workspaceActivities = [<Assignment>this.assignment];
            }
            this.getAssets();
        }

        this.courseStore
            .pipe(select(fromCourseStore.getCurrentAssignment))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((currentAssignment: Assignment | null) => {
                if (currentAssignment && this.maxAttemptsUpdated) {
                    if (this.isComposite()) {
                        this.workspaceActivities =
                            currentAssignment.compositeTaskAssignments;
                    } else {
                        this.workspaceActivities = [
                            <Assignment>this.assignment,
                        ];
                    }
                    this.getAssets();
                }
            });
    }

    ngOnChanges() {
        if (this.assignment) {
            if (this.isCompositeActivity()) {
                this.workspaceActivities = [
                    <CompositeTaskAssignment>this.assignment,
                ];
            } else if (this.isComposite() && this.compositeTaskAssignments) {
                this.workspaceActivities = this.compositeTaskAssignments;
            } else {
                this.workspaceActivities = [<Assignment>this.assignment];
            }
            this.getAssets();
        }
    }

    // Set Practice Attempts Display
    setPracticeAttemptsForDisplay(forCheck: boolean, activity: Assignment) {
        // If eBook, and attempts, and practice attempt then show practice attempts count
        if (
            activity?.asset?.assetTypeId === assetTypeIds.EBOOK_ACT &&
            activity.gradeAttempts
        ) {
            const practiceAttempts = 0;
            const reducer = (item: number, attempt: any, index: number) => {
                if (
                    attempt.attemptID !== null &&
                    activity &&
                    activity.maxAttempts &&
                    index + 1 > activity.maxAttempts
                ) {
                    return item + 1;
                } else {
                    return item;
                }
            };
            const practiceAttemptsTotal = activity.gradeAttempts.reduce(
                reducer,
                practiceAttempts
            );
            if (forCheck && practiceAttemptsTotal > 0) {
                return true;
            } else if (!forCheck && practiceAttemptsTotal === 1) {
                return "1 Practice Attempt";
            } else if (!forCheck && practiceAttemptsTotal > 1) {
                return practiceAttemptsTotal + " Practice Attempts";
            }
        }
    }

    getAssets() {
        if (this.workspaceActivities) {
            this.workspaceActivities.forEach(
                (workspace: Assignment | CompositeTaskAssignment) => {
                    if (workspace.assetId) {
                        this.assetsService
                            .getSelectedAsset(workspace.assetId)
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe((asset) => {
                                if (asset) {
                                    workspace.asset = asset;
                                }
                            });
                    }
                }
            );
        }
    }

    // get if an activity is a composite or not
    isComposite() {
        if (this.assignment) {
            return (
                (<Assignment>this.assignment).shortAssignableType ===
                assetTypeIds.BUNDLE
            );
        }
    }

    isCompositeActivity() {
        return (
            this.assignment &&
            (<CompositeTaskAssignment>this.assignment).compositeTaskAssignmentID
        );
    }

    getWorkspaceTitle(activity: Assignment | CompositeTaskAssignment) {
        if (activity && this.assignment) {
            if (this.assignmentHelper.isCustomSpanishBundle(this.assignment)) {
                return this.assignmentHelper.getSpanishTaskTitle(
                    activity.assetId,
                    this.assignment
                );
            }
            if (this.isComposite() || this.isCompositeActivity()) {
                return activity.asset?.displayName;
            } else {
                return (<Assignment>activity).assignmentName;
            }
        }
    }

    getDate(date: string) {
        return this.dateFormatterHelper.formatDate(date);
    }

    getIconPath(activity: Assignment | CompositeTaskAssignment) {
        if (activity && activity.asset) {
            if (activity.asset.assetActivityTypeId) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    activity.asset.assetActivityTypeId
                );
            } else {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    activity.asset.assetTypeId
                );
            }
        }
    }

    // Format shortAssignableType for display
    getTypeName(activity: Assignment | CompositeTaskAssignment) {
        if (activity && activity.asset) {
            if (activity.asset?.assetActivityTypeId) {
                return this.assetTypeHelper.getCapTypeName(
                    activity.asset?.assetActivityTypeId
                );
            }

            return this.assetTypeHelper.getCapTypeName(
                activity.asset?.assetTypeId
            );
        }
    }

    // Convert seconds into hh:mm:ss
    getDuration(activity: Assignment | CompositeTaskAssignment) {
        if (activity && activity.duration) {
            const duration = activity.duration;
            return this.durationFormat.formatDuration(duration);
        } else {
            return "---";
        }
    }

    resetStatus(compositeTaskAssignmentID: number) {
        this.resetBundleActivityStatusFn.emit(compositeTaskAssignmentID);
    }

    showResetButton(activity: Assignment | CompositeTaskAssignment) {
        if (this.course) {
            if (
                (this.isComposite() || this.isCompositeActivity()) &&
                activity.asset?.assetTypeId !== assetTypeIds.EBOOK_ACT &&
                activity.asset?.assetTypeId !== assetTypeIds.EREADER
            ) {
                return true;
            } else if (
                (this.isComposite() || this.isCompositeActivity()) &&
                activity.asset?.assetTypeId === assetTypeIds.EBOOK_ACT
            ) {
                // If assignment has attempts left
                const maxAttempts = activity.maxAttempts;
                const attempts = activity.gradeAttempts?.length;

                if (
                    (attempts && attempts < maxAttempts) ||
                    maxAttempts === -1
                ) {
                    return true;
                }
            }
        }
    }

    formatAttemptsCount(activity: Assignment | CompositeTaskAssignment) {
        let count = 0;
        if (activity.gradeAttempts) {
            activity.gradeAttempts.forEach((a) => {
                if (a.attemptID) {
                    count = count + 1;
                }
            });
        }
        return count;
    }
    formatMaxAttempts(maxAttempts: number) {
        if (maxAttempts === -1) {
            return "Unlimited";
        } else {
            return maxAttempts;
        }
    }

    disableResetButton(activity: Assignment | CompositeTaskAssignment) {
        if (
            !activity.completedAt ||
            (!activity.startedAt &&
                (activity?.asset?.assetTypeId !== assetTypeIds.EBOOK_ACT ||
                    activity.gradeAttempts?.length === 0))
        ) {
            return true;
        }
    }

    // Launch the dialog box that shows the Workspace iframe
    launchIframe(activity: Assignment | CompositeTaskAssignment) {
        if (activity) {
            const asset: Asset | undefined = activity.asset;
            if (
                asset &&
                asset.assetTypeId === assetTypeIds.READYCHECK &&
                activity.startedAt
            ) {
                this.readyCheckReportDialogRef = this.dialog.open(
                    ReadycheckReportDialogComponent,
                    {
                        data: {
                            assignment: activity,
                            asset: asset,
                            course: this.course,
                        },
                    }
                );
            } else if (asset?.assetTypeId === assetTypeIds.EXT_LINK) {
                const extLink = <ExtLink>asset;
                if (!!this.assignmentHelper.hasLaunchLink(extLink)) {
                    window.open(extLink.launchLink);
                }
            } else if (asset && this.userProfile) {
                this.activityPreviewDialogRef = this.dialog.open(
                    ActivityPreviewDialogComponent,
                    {
                        data: {
                            asset: asset,
                            compositeID: null,
                            compositeType: null,
                            userID: this.userProfile.userID,
                        },
                    }
                );
            }
        }
    }

    getLaunchTitle(activity: Assignment | CompositeTaskAssignment) {
        if (activity) {
            const asset: Asset | undefined = activity.asset;
            if (asset && asset.assetTypeId === assetTypeIds.READYCHECK) {
                return activity.startedAt ? "View Report" : "Preview";
            }
            return "Preview";
        }
    }

    // Max Attempts Section -----------------------------------
    maxAttemptsReached(assignment: Assignment | CompositeTaskAssignment) {
        if (assignment) {
            let reached = false;
            const maxAttempts = assignment.maxAttempts;
            let count = 0;
            assignment.gradeAttempts?.forEach((a) => {
                if (a.attemptID) {
                    count = count + 1;
                }
            });
            if (count >= maxAttempts && maxAttempts !== -1) {
                reached = true;
            }
            return reached;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
